/** 从模板新建：左侧模板卡片（含"空白作品"），右侧变量表单。接口不可用时降级为只能新建空白。 */
import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Empty, Form, Input, Modal, Spin, Typography } from 'antd';
import { FileAddOutlined } from '@ant-design/icons';
import { api, type TemplateSummary } from '../api/client';
import { useTemplateStore } from './template-store';
import { applyVars, buildCreatePayload, defaultVarValues } from './template-vars';

const BLANK = '__blank__';

export function TemplatePicker() {
  const open = useTemplateStore((s) => s.open);
  const finish = useTemplateStore((s) => s.finish);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>(BLANK);
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setLoadError(null);
    api.listTemplates()
      .then((items) => {
        setTemplates(items);
        if (items.length) select(items[0]!);
      })
      .catch((err: Error) => {
        setTemplates([]);
        setLoadError(err.message);
        setSelected(BLANK);
      })
      .finally(() => setLoading(false));
  }, [open]);

  const current = useMemo(() => templates.find((t) => t.id === selected), [templates, selected]);

  function select(t: TemplateSummary) {
    setSelected(t.id);
    setValues(defaultVarValues(t.vars ?? []));
  }

  const onConfirm = () => {
    if (selected === BLANK || !current) { finish({ kind: 'blank' }); return; }
    const payload = buildCreatePayload(current.id, current.vars ?? [], values);
    finish({ kind: 'template', templateId: payload.templateId, vars: payload.vars });
  };

  const previewTitle = current ? applyVars(current.title, { ...defaultVarValues(current.vars ?? []), ...values }) : '空白作品';

  return (
    <Modal
      open={open}
      title="新建作品"
      width={860}
      closable={false}
      maskClosable={false}
      destroyOnClose
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button onClick={() => finish({ kind: 'blank' })}>空白作品</Button>
          <Button type="primary" onClick={onConfirm} disabled={loading}>
            {selected === BLANK ? '创建空白作品' : '使用此模板'}
          </Button>
        </div>
      }
    >
      {loadError && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="模板列表加载失败，可先创建空白作品" description={loadError} />}
      <div style={{ display: 'flex', gap: 16, minHeight: 360 }}>
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, alignContent: 'start' }}>
          <TemplateCard
            active={selected === BLANK}
            title="空白作品"
            description="从零开始"
            onClick={() => setSelected(BLANK)}
            icon={<FileAddOutlined style={{ fontSize: 36, color: '#999' }} />}
          />
          {loading && <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 24 }}><Spin /></div>}
          {!loading && !loadError && templates.length === 0 && (
            <div style={{ gridColumn: '1 / -1' }}><Empty description="暂无模板" image={Empty.PRESENTED_IMAGE_SIMPLE} /></div>
          )}
          {templates.map((t) => (
            <TemplateCard
              key={t.id}
              active={selected === t.id}
              title={applyVars(t.title, defaultVarValues(t.vars ?? []))}
              description={t.description}
              cover={t.cover ?? undefined}
              onClick={() => select(t)}
            />
          ))}
        </div>
        <div style={{ width: 300, borderLeft: '1px solid #f0f0f0', paddingLeft: 16 }}>
          <Typography.Title level={5} style={{ marginTop: 0 }}>{previewTitle}</Typography.Title>
          {current && current.vars?.length ? (
            <Form layout="vertical" size="small">
              {current.vars.map((v) => (
                <Form.Item key={v.key} label={v.label}>
                  <Input
                    value={values[v.key] ?? ''}
                    placeholder={v.placeholder ?? v.default}
                    onChange={(e) => setValues((prev) => ({ ...prev, [v.key]: e.target.value }))}
                  />
                </Form.Item>
              ))}
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>留空则使用默认值。创建后仍可在编辑器里修改任何文字。</Typography.Text>
            </Form>
          ) : (
            <Typography.Text type="secondary">{current ? '此模板没有可填写的变量。' : '不使用模板，创建一个空白作品。'}</Typography.Text>
          )}
        </div>
      </div>
    </Modal>
  );
}

function TemplateCard({ active, title, description, cover, icon, onClick }: {
  active: boolean; title: string; description: string; cover?: string; icon?: React.ReactNode; onClick(): void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        cursor: 'pointer', borderRadius: 8, overflow: 'hidden',
        border: `2px solid ${active ? '#1677ff' : '#f0f0f0'}`, background: '#fff',
      }}
    >
      <div style={{ height: 150, background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {cover ? <img src={cover} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : icon}
      </div>
      <div style={{ padding: '8px 10px' }}>
        <div style={{ fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
        <div style={{ fontSize: 12, color: '#999', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{description}</div>
      </div>
    </div>
  );
}
